import { useAuthStore } from '@/stores/authStore'
import { showToast } from '@/components/ui/Toast'

const API_BASE = import.meta.env.VITE_API_BASE || 'https://cal-api.huii.top'

// Smart cache with different TTLs based on data type
const cache = new Map<string, { data: unknown; expires: number }>()
const CACHE_TTL = {
  short: 15000,    // 15s - frequently changing data
  medium: 60000,   // 1min - moderately stable data
  long: 300000,    // 5min - stable data
}

function getCacheTTL(url: string): number {
  // Stable data (user info, spaces list, tags)
  if (url.includes('/api/me') || url.includes('/api/spaces') || url.includes('/api/tags')) {
    return CACHE_TTL.long
  }
  // Moderately stable data (activities, events)
  if (url.includes('/api/activities') || url.includes('/api/events')) {
    return CACHE_TTL.medium
  }
  // Frequently changing data (notifications, period)
  return CACHE_TTL.short
}

class ApiClient {
  private _token: string | null = null
  private _vaultToken: string | null = null

  get token() { return this._token }
  set token(val: string | null) { this._token = val }

  get vaultToken() { return this._vaultToken }
  set vaultToken(val: string | null) { this._vaultToken = val }

  clearCache() {
    cache.clear()
  }

  private async request<T>(url: string, opts?: RequestInit): Promise<T> {
    if (!navigator.onLine) {
      throw new Error('网络已断开，请检查网络连接')
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (this._token) headers['Authorization'] = `Bearer ${this._token}`
    if (this._vaultToken) headers['X-Vault-Token'] = this._vaultToken

    const fullUrl = url.startsWith('http') ? url : API_BASE + url
    
    // Check cache for GET requests
    const isGet = !opts?.method || opts.method === 'GET'
    if (isGet) {
      const cached = cache.get(fullUrl)
      if (cached && cached.expires > Date.now()) {
        return cached.data as T
      }
    }

    let res: Response
    try {
      res = await fetch(fullUrl, { ...opts, headers })
    } catch {
      throw new Error('网络请求失败，请检查网络连接')
    }

    if (res.status === 401) {
      const isAuthEndpoint = url.includes('/login') || url.includes('/register') || url.includes('/verify') || url.includes('/vault/')
      if (!isAuthEndpoint) {
        showToast('登录已过期，请重新登录')
        useAuthStore.getState().logout()
      }
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error || 'Unauthorized')
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error || getDefaultErrorMessage(res.status))
    }

    const data = await res.json()
    
    // Cache GET responses with smart TTL
    if (isGet) {
      const ttl = getCacheTTL(url)
      cache.set(fullUrl, { data, expires: Date.now() + ttl })
    }
    
    return data
  }

  get<T>(url: string) {
    return this.request<T>(url)
  }

  post<T>(url: string, body: unknown) {
    cache.clear() // Clear cache on mutations
    return this.request<T>(url, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  patch<T>(url: string, body: unknown) {
    cache.clear()
    return this.request<T>(url, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  }

  put<T>(url: string, body: unknown) {
    cache.clear()
    return this.request<T>(url, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  delete<T>(url: string) {
    cache.clear()
    return this.request<T>(url, { method: 'DELETE' })
  }
}

export const api = new ApiClient()

function getDefaultErrorMessage(status: number): string {
  const messages: Record<number, string> = {
    400: '请求参数有误',
    401: '登录已过期，请重新登录',
    403: '没有权限执行此操作',
    404: '请求的内容不存在',
    500: '服务器错误，请稍后重试',
  }
  return messages[status] || `请求失败 (${status})`
}
